<template>
  <div class="pendulum-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$T = 2π√(L/g) (θ < 5°)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>即时摆角 θ</label>
            <span class="val">{{ (theta * 180 / Math.PI).toFixed(1) }}°</span>
          </div>
          <div class="hud-item orange">
             <label>系统周期 T</label>
             <span class="val">{{ period.toFixed(2) }} <small>s</small></span>
          </div>
        </div>

        <svg width="100%" height="260" viewBox="0 0 400 260" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-bob"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <radialGradient id="bob-light" cx="30%" cy="30%" r="50%"><stop offset="0%" stop-color="#fff" /><stop offset="100%" stop-color="#475569" /></radialGradient>
          </defs>

          <!-- 能量转化指示条 (Left) -->
          <g transform="translate(30, 80)">
             <rect width="10" height="100" fill="#1e293b" rx="2" />
             <!-- 势能 Ep -->
             <rect x="0" :y="100 - ep*1.5" width="4" :height="ep*1.5" fill="#38bdf8" rx="1" />
             <!-- 动能 Ek -->
             <rect x="6" :y="100 - ek*1.5" width="4" :height="ek*1.5" fill="#f43f5e" rx="1" />
             <text x="5" y="115" text-anchor="middle" fill="#475569" font-size="7">E_tot</text>
          </g>

          <!-- 相空间轨迹图 (Right) -->
          <g transform="translate(280, 130)">
             <line x1="-50" y1="0" x2="50" y2="0" stroke="#1e293b" />
             <line x1="0" y1="-50" x2="0" y2="50" stroke="#1e293b" />
             <!-- 轨迹带 -->
             <path :d="phasePath" fill="none" stroke="#10b981" stroke-width="1.5" opacity="0.6" />
             <circle :cx="theta * 40" :cy="-omega * 100" r="3" fill="#10b981" filter="url(#glow-bob)" />
             <text x="0" y="65" text-anchor="middle" fill="#475569" font-size="8">相空间 (θ - ω)</text>
          </g>

          <!-- 支架 -->
          <line x1="160" y1="20" x2="240" y2="20" stroke="#334155" stroke-width="3" />

          <!-- 摆绳 -->
          <line x1="200" y1="20" :x2="bobX" :y2="bobY" stroke="#94a3b8" stroke-width="1.2" />

          <!-- 摆球 (Bob) -->
          <circle :cx="bobX" :cy="bobY" r="12" fill="url(#bob-light)" filter="url(#glow-bob)" />
          
          <!-- 回复力矢量 -->
          <g v-if="running" :transform="`translate(${bobX}, ${bobY})`">
             <line x1="0" y1="0" :x2="-Math.sin(theta) * 30" :y2="Math.cos(theta) * 30" 
                   stroke="#fbbf24" stroke-width="2" marker-end="url(#arr-f)" />
          </g>

          <defs>
             <marker id="arr-f" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#fbbf24" /></marker>
          </defs>
        </svg>

        <div class="physics-caption">
           {{ Math.abs(theta) < 0.1 ? '靠近平衡点：动能 Ek 最大' : '靠近最高点：势能 Ep 最大' }}
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">摆长 (L)</label>
                 <el-slider v-model="L" :min="50" :max="180" size="small"></el-slider>
              </div>
              <div class="flex-1">
                 <label class="s-label">初始能量 (θ₀)</label>
                 <el-slider v-model="initTheta" :min="0" :max="90" size="small" @change="reset"></el-slider>
              </div>
              <el-button :type="running ? 'warning' : 'primary'" size="mini" plain @click="running = !running">
                {{ running ? '暂停' : '释放运动' }}
              </el-button>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-timer"></i>
           <p><strong>核心揭示：</strong>单摆是简谐运动的活化石。观察右侧的<strong>相空间圆环</strong>：它展示了位移与速度在守恒系统中的周期化演变。<strong>物理发现：</strong>伽利略提出的等时性仅在小角度 θ 下成立。当你增大初始角至 60° 以上，你会发现周期 T 会由于大角非线性而略微变长。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'SimplePendulumViz',
  data() {
    return {
      L: 150,
      initTheta: 30,
      theta: 0.52, // 30 deg in rad
      omega: 0,
      running: true,
      history: [],
      maxHistory: 60
    }
  },
  computed: {
    bobX() { return 200 + Math.sin(this.theta) * this.L; },
    bobY() { return 20 + Math.cos(this.theta) * this.L; },
    period() { return 2 * Math.PI * Math.sqrt(this.L / 400); }, // Scaled G
    ep() { return 60 * (1 - Math.cos(this.theta)); },
    ek() { return 0.5 * this.omega * this.omega * 50000; },
    phasePath() {
       if (this.history.length < 2) return "";
       return "M " + this.history.map(p => `${p.t * 40},${-p.w * 100}`).join(" L ");
    }
  },
  methods: {
    reset() {
       this.theta = this.initTheta * Math.PI / 180;
       this.omega = 0;
       this.history = [];
    },
    animate() {
       if (this.running) {
          const dt = 0.16;
          // Accel = -(g/L) sin(theta) | Numerical integration for large angles
          const accel = -(25 / this.L) * Math.sin(this.theta);
          this.omega += accel * dt;
          this.theta += this.omega * dt;
          
          // Phase space history
          this.history.push({ t: this.theta, w: this.omega });
          if (this.history.length > this.maxHistory) this.history.shift();
       }
       this.animId = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { cancelAnimationFrame(this.animId); }
}
</script>

<style scoped lang="scss">
.pendulum-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #dbeafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 260px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; } &.cyan { border-left: 3px solid #0ea5e9; } &.orange { border-left: 3px solid #f97316; } } }
.physics-caption { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #334155; font-size: 11px; font-weight: bold; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex { display: flex; } .items-center { align-items: center; } .flex-row { flex-direction: row; } .flex-1 { flex: 1; } .gap-6 { gap: 24px; }
.discovery-card-compact { background: #eff6ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #dbeafe; display: flex; align-items: center; gap: 10px; i { color: #2563eb; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #1e40af; line-height: 1.5; strong { color: #1e3a8a; } } }
</style>
